// Mesón · fase 2a (docs/frontend/19): MI TURNO DE CAJA. El cajero abre su turno en el mesón con el
// fondo inicial, ve sus cobros uno a uno, registra retiros de efectivo a tesorería (Alt+R) o ingresos
// de sencillo (Alt+I) y CIERRA A CIEGAS (F9): cuenta el efectivo de la gaveta, el cierre de lote del
// Getnet (débito y crédito) y los cheques, sin ver lo esperado. Después del cierre ve su arqueo
// (esperado, contado, diferencia) y el comprobante para imprimir y entregar con el dinero.
//
// 0130 · turno COMPARTIDO: la responsable habilita en su turno, desde la lista de ELEGIBLES de la caja
// (los define administración), a quienes cobran con ella; puede hacerlo al abrir, en un paso. El
// operador ve el turno y sus cobros, pero NO cierra, no arquea ni mueve efectivo: eso es de la
// responsable. Con política de cierre, el resultado dice si el cierre fue normal o quedó como excepción.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import {
  claves,
  useElegiblesPunto,
  useElegiblesTurno,
  usePuntosCobro,
  useTurnoActual,
} from '../api/consultas';
import type {
  CierreTurno,
  CodigoCausa,
  ElegibleTurno,
  MedioCaja,
  PuntoCobro,
  TurnoCaja,
} from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useAtajos } from '../shell/atajos';
import { DocumentosNota } from '../dte/DocumentosNota';
import { useDocumentosEmitidos } from '../dte/documentosEmitidos';
import { motivosEnTexto, NOMBRE_CAUSA, nombreCola, senalesEnTexto } from './textos';

export const NOMBRE_MEDIO_CAJA: Record<MedioCaja, string> = {
  EFECTIVO: 'Efectivo',
  DEBITO: 'Débito (Getnet)',
  CREDITO: 'Crédito (Getnet)',
  CHEQUE: 'Cheques',
  TRANSFERENCIA: 'Transferencias',
};

/** Pesos enteros desde lo digitado («12.500» → "12500"); null si no es un monto. */
export const enteroCaja = (t: string): string | null => {
  const v = t.replace(/[.,\s$]/g, '');
  return /^\d{1,15}$/.test(v) ? String(Number(v)) : null;
};

export function MiTurno({ activa }: { activa: boolean }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const turno = useTurnoActual(empresaId, activa);
  const [cierre, setCierre] = useState<CierreTurno | null>(null);
  return (
    <div className="pantalla consulta caja-turno" data-pantalla="mi-turno">
      {turno.isError && <PanelError error={turno.error} />}
      {cierre ? (
        <ResultadoCierre cierre={cierre} alSeguir={() => setCierre(null)} />
      ) : turno.isSuccess && turno.data ? (
        <TurnoAbierto activa={activa} turno={turno.data} alCerrar={setCierre} />
      ) : turno.isSuccess ? (
        <AbrirTurno empresaId={empresaId} />
      ) : null}
    </div>
  );
}

/**
 * Abrir el turno: aquí y en el punto de venta (Rodrigo, 2026-10-24: «llegan, entran, venden y les
 * sale el aviso»), donde va dentro de un diálogo (`enDialogo`: sin título propio) y avisa al abrir.
 */
export function AbrirTurno({
  empresaId,
  enDialogo = false,
  alAbierto,
}: {
  empresaId: string;
  enDialogo?: boolean;
  alAbierto?: () => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const intentosCompartir = useIntentos();
  const { usuario } = useSesion();
  const puntos = usePuntosCobro(empresaId);
  // 0133 (Rodrigo, 2026-11-01: «solo quiero que existan esas 4 cajas»): las cajas personales no se
  // ofrecen; el mesón de cada puesto primero y la de tesorería al final (se ve, pero se abre desde
  // Tesorería).
  const ofrecidos = (puntos.data ?? [])
    .filter((p) => !p.usuarioId && p.tipo !== 'PERSONAL')
    .sort((a, b) => ordenCaja(a) - ordenCaja(b) || a.codigo.localeCompare(b.codigo));
  const libres = ofrecidos.filter((p) => p.activo && !p.ocupadoPor && p.tipo !== 'TESORERIA');
  // 0133: este equipo recuerda en qué puesto está (se guarda al abrir o compartir).
  const [puestoEquipo] = useState(leerPuestoEquipo);
  const recordarPuesto = (p: PuntoCobro | undefined) => {
    if (p?.puesto) guardarPuestoEquipo(p.puesto);
  };
  // 0132 · compartir caja (Rodrigo, 2026-11-01): una caja ya abierta se comparte en vez de abrir otra.
  const compartir = useMutation({
    mutationFn: (p: PuntoCobro) =>
      api.post(
        `/caja/turnos/${p.turnoId}/compartir`,
        {},
        { idempotencia: intentosCompartir.clave('compartir-turno', { turnoId: p.turnoId }) },
      ),
    onSuccess: async (_r, p) => {
      intentosCompartir.cerrar();
      recordarPuesto(p);
      await qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
      alAbierto?.();
    },
    onError: (e) => intentosCompartir.cerrar(e),
  });
  const [puntoId, setPuntoId] = useState('');
  const [fondo, setFondo] = useState('');
  const elegido =
    puntoId ||
    libres.find((p) => puestoEquipo !== null && p.puesto === puestoEquipo)?.id ||
    libres[0]?.id ||
    '';
  const monto = enteroCaja(fondo);
  // 0130: los elegibles de la caja; la responsable marca a quienes habilita en este turno. V2: vienen
  // PRESELECCIONADOS los del turno anterior que siguen elegibles y activos; nada se activa sin confirmar.
  const elegibles = useElegiblesPunto(empresaId, elegido || null);
  const [operadores, setOperadores] = useState<string[] | null>(null);
  const vigentes = elegibles.data?.punto.compartible ? elegibles.data.vigentes : [];
  const sugeridos = elegibles.data?.sugeridos ?? [];
  const marcados = (operadores ?? sugeridos).filter((o) => vigentes.some((v) => v.usuarioId === o));
  const abrir = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        puntoId: elegido,
        fondoInicial: monto!,
        ...(marcados.length ? { operadores: marcados } : {}),
      };
      return api.post('/caja/turnos', cuerpo, {
        idempotencia: intentos.clave('abrir-turno', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      recordarPuesto(libres.find((p) => p.id === elegido));
      await qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
      alAbierto?.();
    },
    onError: (e) => intentos.cerrar(e),
  });
  const valido = !!elegido && monto !== null && !abrir.isPending;
  return (
    <form
      className="caja-abrir"
      aria-label="Abrir turno de caja"
      onSubmit={(e) => {
        e.preventDefault();
        if (valido) abrir.mutate();
      }}
    >
      {!enDialogo && <h2>Abrir o compartir caja</h2>}
      <p className="apagado">
        Elige tu puesto. Si su caja ya está abierta, compártela: cobras en ella a tu nombre y quien
        la abrió la cierra. Si no, ábrela con el fondo de la gaveta (el sencillo con que partes); al
        cerrar se cuenta a ciegas y el sistema compara.
      </p>
      {puntos.isSuccess && !ofrecidos.length && (
        <p className="aviso-linea tono-aviso" role="alert">
          La empresa no tiene cajas. Pide a administración que cree una.
        </p>
      )}
      {ofrecidos.length > 0 && (
        <table className="grilla compacta caja-lista" aria-label="Cajas">
          <thead>
            <tr>
              <th />
              <th>¿En qué puesto estás?</th>
              <th>Caja</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ofrecidos.map((p) => {
              const libre = libres.includes(p);
              const miPuesto = puestoEquipo !== null && p.puesto === puestoEquipo;
              return (
                <tr
                  key={p.id}
                  data-caja={p.codigo}
                  data-mi-puesto={miPuesto || undefined}
                  className={libre ? undefined : 'apagado'}
                >
                  <td>
                    {libre && (
                      <input
                        type="radio"
                        name="caja-abrir"
                        aria-label={`Abrir ${p.codigo}`}
                        checked={elegido === p.id}
                        onChange={() => {
                          setPuntoId(p.id);
                          setOperadores(null);
                        }}
                      />
                    )}
                  </td>
                  <td>
                    {p.tipo === 'TESORERIA' ? (
                      'Tesorería'
                    ) : p.puesto ? (
                      <b>Puesto {p.puesto}</b>
                    ) : (
                      'Sin puesto'
                    )}
                    {miPuesto ? ' · este equipo' : ''}
                  </td>
                  <td>
                    {p.codigo} · {p.nombre}
                  </td>
                  <td>{estadoCaja(p)}</td>
                  <td className="al-der">
                    {p.turnoId && p.turnoCompartible && (
                      <button
                        type="button"
                        disabled={compartir.isPending}
                        onClick={() => compartir.mutate(p)}
                      >
                        {compartir.isPending && compartir.variables?.id === p.id
                          ? 'Compartiendo…'
                          : 'Compartir caja'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {compartir.isError && <PanelError error={compartir.error} />}
      {puntos.isSuccess && ofrecidos.length > 0 && !libres.length && (
        <p className="aviso-linea" role="note">
          No hay cajas libres: comparte una abierta con «Compartir caja».
        </p>
      )}
      {libres.length > 0 && (
        <label className="campo">
          <span className="etiqueta">Fondo inicial (pesos)</span>
          <input
            aria-label="Fondo inicial"
            autoFocus
            inputMode="numeric"
            className="cobro-monto"
            value={fondo}
            onChange={(e) => setFondo(e.target.value)}
          />
        </label>
      )}
      {vigentes.length > 0 && (
        <fieldset className="campo caja-elegibles" aria-label="Operadores del turno">
          <legend className="etiqueta">
            Responsable: {usuario?.nombre ?? 'tú'} ·{' '}
            {sugeridos.length ? 'Operadores sugeridos' : 'Operadores de este turno'}
          </legend>
          {vigentes.map((v) => (
            <label key={v.usuarioId} data-sugerido={sugeridos.includes(v.usuarioId)}>
              <input
                type="checkbox"
                checked={marcados.includes(v.usuarioId)}
                onChange={(e) =>
                  setOperadores(
                    e.target.checked
                      ? [...marcados, v.usuarioId]
                      : marcados.filter((o) => o !== v.usuarioId),
                  )
                }
              />{' '}
              {v.nombre}
              {sugeridos.includes(v.usuarioId) ? ' (operó el turno anterior)' : ''}
              {v.funcion === 'OPERADOR_EXCEPCIONAL' ? ' (excepcional)' : ''}
            </label>
          ))}
          <span className="apagado">
            Cobran contra tu turno; tú cierras y arqueas por todos. Solo quedan habilitados los que
            dejes marcados al confirmar.
          </span>
        </fieldset>
      )}
      {abrir.isError && <PanelError error={abrir.error} />}
      {libres.length > 0 && (
        <button type="submit" className="primario" disabled={!valido}>
          {abrir.isPending
            ? 'Abriendo…'
            : vigentes.length
              ? 'Confirmar y abrir (Enter)'
              : `Abrir ${libres.find((p) => p.id === elegido)?.codigo ?? 'caja'} (Enter)`}
        </button>
      )}
    </form>
  );
}

/** 0133 · orden de la lista: el mesón de cada puesto (1, 2, 3…), los mesones sin puesto y tesorería. */
const ordenCaja = (p: PuntoCobro) => (p.tipo === 'TESORERIA' ? 1000 : p.puesto ? p.puesto : 500);

/** 0133 · el puesto de este equipo (lo recuerda el navegador; si no se puede leer, ninguno). */
const CLAVE_PUESTO = 'madeci.caja.puesto';
function leerPuestoEquipo(): number | null {
  try {
    const v = Number(window.localStorage.getItem(CLAVE_PUESTO));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}
function guardarPuestoEquipo(puesto: number) {
  try {
    window.localStorage.setItem(CLAVE_PUESTO, String(puesto));
  } catch {
    // Sin almacenamiento del navegador, se vuelve a elegir la próxima vez.
  }
}

/** 0132 · el estado de una caja en la lista: libre, o abierta por quién y con quiénes la comparten. */
function estadoCaja(p: PuntoCobro): string {
  if (!p.activo) return 'Inactiva';
  if (p.ocupadoPor) {
    const con = (p.compartenCon ?? []).map((c) => c.nombre);
    return `Abierta por ${p.ocupadoPor}${con.length ? ` · comparte: ${con.join(', ')}` : ''}`;
  }
  if (p.tipo === 'TESORERIA')
    return p.titular ? `De ${p.titular} · se abre desde Tesorería` : 'Se abre desde Tesorería';
  return 'Libre';
}

function TurnoAbierto({
  activa,
  turno,
  alCerrar,
}: {
  activa: boolean;
  turno: TurnoCaja;
  alCerrar: (c: CierreTurno) => void;
}) {
  const [dialogo, setDialogo] = useState<null | 'RETIRO' | 'INGRESO' | 'cerrar'>(null);
  // V4: pedir que tesorería anule un cobro del turno (no se anula desde la caja).
  const { puede } = useSesion();
  const puedePedir = puede('caja.recibo.solicitar_anulacion');
  const [pedirAnulacion, setPedirAnulacion] = useState<TurnoCaja['recibos'][number] | null>(null);
  // 0130: el operador de un turno compartido cobra, pero no cierra, no arquea ni mueve efectivo.
  const operador = turno.miRol === 'OPERADOR';
  const compartido = (turno.operadores ?? []).length > 0;
  useAtajos(activa && dialogo === null && !operador, {
    'Alt+R': () => setDialogo('RETIRO'),
    'Alt+I': () => setDialogo('INGRESO'),
    F9: () => setDialogo('cerrar'),
  });
  const vigentes = turno.recibos.filter((r) => r.estado === 'VIGENTE');
  // Cada cobro se reconoce por su boleta o factura (2026-10-26), no por la nota interna.
  const notaIds = [...new Set(turno.recibos.flatMap((r) => (r.notaId ? [r.notaId] : [])))];
  const docs = useDocumentosEmitidos(
    turno.empresaId,
    { notaIds: notaIds.slice(0, 200).join(',') },
    notaIds.length > 0,
  );
  const docsDeNota = (notaId: string | null) =>
    (docs.data?.items ?? []).filter(
      (d) => d.origen?.notaId === notaId && [33, 34, 39, 41].includes(d.tipoDte),
    );
  return (
    <>
      <header className="caja-cab" data-turno={turno.numeroVisible}>
        <h2>
          Turno {turno.numeroVisible} · {turno.punto.codigo} {turno.punto.nombre}
        </h2>
        <span className="insignia" data-estado-turno="ABIERTO">
          abierto desde {fechaHora(turno.abiertoEn)}
        </span>
        <span>
          Fondo inicial <b>{pesos(turno.fondoInicial)}</b>
        </span>
        <span>
          <b>{vigentes.length}</b> cobro{vigentes.length === 1 ? '' : 's'}
        </span>
        {operador && (
          <span className="insignia" data-rol-turno="OPERADOR">
            operas esta caja · responsable {turno.responsable?.nombre ?? turno.cajero}
          </span>
        )}
      </header>
      {operador && (
        <div className="aviso-linea caja-comparto" role="note">
          <span>
            Tus cobros quedan en este turno a tu nombre. El cierre y el arqueo los hace la
            responsable.
          </span>
          <DejarCaja turno={turno} />
        </div>
      )}
      {!operador && <CompartenCaja turno={turno} />}
      {turno.legado && (
        <p className="aviso-linea" role="note">
          Turno de una cuenta compartida de puesto (legado): la persona que cobra es desconocida y
          no se pueden habilitar operadoras. El turno compartido exige una responsable personal con
          su propia cuenta.
        </p>
      )}
      {!operador && !turno.legado && <OperadoresTurno turno={turno} />}
      <section aria-label="Cobros del turno">
        <h3>Cobros del turno</h3>
        <table className="grilla compacta">
          <thead>
            <tr>
              <th>Hora</th>
              <th>Recibo</th>
              <th>Documento</th>
              <th>Medios</th>
              {compartido && <th>Cobró</th>}
              <th className="al-der">Total</th>
              {puedePedir && <th />}
            </tr>
          </thead>
          <tbody>
            {turno.recibos.map((r) => (
              <tr key={r.id} className={r.estado === 'ANULADO' ? 'anulado' : undefined}>
                <td>{fechaHora(r.creadoEn)}</td>
                <td className="mono">{r.numeroVisible}</td>
                <td>
                  <DocumentosNota documentos={docsDeNota(r.notaId)} vacio={r.nota ?? '—'} />
                </td>
                <td>
                  {r.medios.map((m) => NOMBRE_MEDIO_CAJA[m.medio].replace(/s$/, '')).join(' + ')}
                  {r.estado === 'ANULADO' && <span className="insignia"> anulado</span>}
                  {r.anulacionPendiente && (
                    <span className="insignia" data-anulacion="PENDIENTE">
                      {' '}
                      anulación pedida
                    </span>
                  )}
                </td>
                {compartido && <td>{r.cobradoPor?.nombre ?? '—'}</td>}
                <td className="al-der">{pesos(r.total)}</td>
                {puedePedir && (
                  <td className="al-der">
                    {r.estado === 'VIGENTE' && !r.anulacionPendiente && (
                      <button type="button" onClick={() => setPedirAnulacion(r)}>
                        Pedir anulación…
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {!turno.recibos.length && (
              <tr>
                <td colSpan={(compartido ? 6 : 5) + (puedePedir ? 1 : 0)} className="grilla-vacia">
                  Todavía no hay cobros en este turno (F10 en una nota guardada)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      {!!turno.movimientos.length && (
        <section aria-label="Retiros e ingresos">
          <h3>Retiros e ingresos de efectivo</h3>
          <table className="grilla compacta">
            <tbody>
              {turno.movimientos.map((m) => (
                <tr key={m.id}>
                  <td>{fechaHora(m.creadoEn)}</td>
                  <td>{m.tipo === 'RETIRO' ? 'Retiro' : 'Ingreso'}</td>
                  <td>{m.motivo}</td>
                  <td className="al-der">
                    {m.tipo === 'RETIRO' ? '−' : '+'}
                    {pesos(m.monto)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {!operador && (
        <footer className="acciones">
          <button type="button" onClick={() => setDialogo('RETIRO')}>
            <kbd>Alt+R</kbd>Retiro de efectivo
          </button>
          <button type="button" onClick={() => setDialogo('INGRESO')}>
            <kbd>Alt+I</kbd>Ingreso de sencillo
          </button>
          <button type="button" className="primario" onClick={() => setDialogo('cerrar')}>
            <kbd>F9</kbd>Cerrar turno
          </button>
        </footer>
      )}
      {(dialogo === 'RETIRO' || dialogo === 'INGRESO') && (
        <MovimientoTurno turno={turno} tipo={dialogo} alCerrar={() => setDialogo(null)} />
      )}
      {pedirAnulacion && (
        <PedirAnulacion
          turno={turno}
          recibo={pedirAnulacion}
          alCerrar={() => setPedirAnulacion(null)}
        />
      )}
      {dialogo === 'cerrar' && (
        <CerrarTurno
          turno={turno}
          alCancelar={() => setDialogo(null)}
          alCerrar={(c) => {
            setDialogo(null);
            alCerrar(c);
          }}
        />
      )}
    </>
  );
}

/**
 * V4 · REQUEST_PAYMENT_VOID: la responsable o una operadora del turno pide que tesorería anule un cobro,
 * con un código de causa de un clic (texto solo con «Otra»). No anula nada: lo ejecuta tesorería.
 */
function PedirAnulacion({
  turno,
  recibo,
  alCerrar,
}: {
  turno: TurnoCaja;
  recibo: TurnoCaja['recibos'][number];
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const [causa, setCausa] = useState<CodigoCausa | null>(null);
  const [nota, setNota] = useState('');
  const pedir = useMutation({
    mutationFn: () =>
      api.post(`/caja/recibos/${recibo.id}/solicitar-anulacion`, {
        causa,
        ...(causa === 'OTHER' && nota.trim() ? { nota: nota.trim() } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
      alCerrar();
    },
  });
  const falta = !causa || (causa === 'OTHER' && !nota.trim());
  return (
    <Modal
      titulo={`Pedir anulación · ${recibo.numeroVisible}`}
      ancho="angosto"
      alCerrar={() => !pedir.isPending && alCerrar()}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={pedir.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={pedir.isPending || falta}
            onClick={() => pedir.mutate()}
          >
            {pedir.isPending ? 'Enviando…' : 'Pedir a tesorería'}
          </button>
        </>
      }
    >
      <p className="apagado">
        {pesos(recibo.total)} · lo anula tesorería; queda a tu nombre que lo pediste.
      </p>
      <fieldset className="campo caja-revision">
        <legend className="etiqueta">Causa</legend>
        {(Object.keys(NOMBRE_CAUSA) as CodigoCausa[]).map((c) => (
          <label key={c}>
            <input
              type="radio"
              name="causa-anulacion"
              checked={causa === c}
              onChange={() => setCausa(c)}
            />{' '}
            {NOMBRE_CAUSA[c]}
          </label>
        ))}
      </fieldset>
      {causa === 'OTHER' && (
        <label className="campo">
          <span className="etiqueta">Qué pasó (obligatorio con «Otra»)</span>
          <input
            aria-label="Nota de la solicitud"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
        </label>
      )}
      {pedir.isError && <PanelError error={pedir.error} />}
    </Modal>
  );
}

const ESTADO_ELEGIBLE: Record<ElegibleTurno['estado'], string> = {
  HABILITADO: 'habilitado en este turno',
  DISPONIBLE: 'disponible',
  CON_TURNO_PROPIO: 'tiene su propia caja abierta',
  EN_OTRA_CAJA: 'opera otra caja',
};

/**
 * 0130 · la responsable habilita o retira, desde la lista de ELEGIBLES de su caja, a quienes cobran
 * en su turno. No crea operadores: si alguien no está en la lista, lo agrega administración de cajas.
 */
function OperadoresTurno({ turno }: { turno: TurnoCaja }) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const elegibles = useElegiblesTurno(turno.empresaId, turno.id);
  const refrescar = () => qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
  const habilitar = useMutation({
    mutationFn: (usuarioId: string) =>
      api.post(
        `/caja/turnos/${turno.id}/operadores`,
        { usuarioId },
        { idempotencia: intentos.clave('habilitar-operador', { turno: turno.id, usuarioId }) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      await refrescar();
    },
    onError: (e) => intentos.cerrar(e),
  });
  const retirar = useMutation({
    mutationFn: (usuarioId: string) =>
      api.post(
        `/caja/turnos/${turno.id}/operadores/${usuarioId}/retirar`,
        {},
        { idempotencia: intentos.clave('retirar-operador', { turno: turno.id, usuarioId }) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      await refrescar();
    },
    onError: (e) => intentos.cerrar(e),
  });
  const lista = elegibles.data ?? [];
  if (!lista.length) return null;
  const ocupado = habilitar.isPending || retirar.isPending;
  return (
    <section className="recuadro" aria-label="Operadores del turno">
      <h3>Operadores del turno</h3>
      <table className="grilla compacta">
        <thead>
          <tr>
            <th>Persona (elegible de la caja)</th>
            <th>Estado</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lista.map((e) => (
            <tr key={e.usuarioId} data-elegible={e.estado}>
              <td>{e.nombre}</td>
              <td>
                {ESTADO_ELEGIBLE[e.estado]}
                {e.excepcional ? ' · excepcional' : ''}
                {e.otraCaja ? ` (${e.otraCaja})` : ''}
              </td>
              <td className="al-der">
                {e.estado === 'HABILITADO' ? (
                  <button
                    type="button"
                    disabled={ocupado}
                    onClick={() => retirar.mutate(e.usuarioId)}
                  >
                    Retirar de mi turno
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={ocupado || e.estado !== 'DISPONIBLE'}
                    onClick={() => habilitar.mutate(e.usuarioId)}
                  >
                    Habilitar en mi turno
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {habilitar.isError && <PanelError error={habilitar.error} />}
      {retirar.isError && <PanelError error={retirar.error} />}
    </section>
  );
}

/** 0132 · quien comparte la caja la deja: sus cobros siguientes vuelven a ser como antes de compartir. */
function DejarCaja({ turno }: { turno: TurnoCaja }) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const dejar = useMutation({
    mutationFn: () =>
      api.post(
        `/caja/turnos/${turno.id}/dejar`,
        {},
        { idempotencia: intentos.clave('dejar-caja', { turno: turno.id }) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });
  return (
    <>
      <button type="button" disabled={dejar.isPending} onClick={() => dejar.mutate()}>
        {dejar.isPending ? 'Dejando…' : 'Dejar la caja'}
      </button>
      {dejar.isError && <PanelError error={dejar.error} />}
    </>
  );
}

/** 0132 · la responsable ve quiénes comparten su caja y puede sacar a alguien. */
function CompartenCaja({ turno }: { turno: TurnoCaja }) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const sacar = useMutation({
    mutationFn: (usuarioId: string) =>
      api.post(
        `/caja/turnos/${turno.id}/operadores/${usuarioId}/retirar`,
        {},
        { idempotencia: intentos.clave('retirar-operador', { turno: turno.id, usuarioId }) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });
  const activos = (turno.operadores ?? []).filter((o) => o.compartio && !o.retiradoEn);
  if (!activos.length) return null;
  return (
    <section className="recuadro" aria-label="Comparten la caja">
      <h3>Comparten la caja</h3>
      <table className="grilla compacta">
        <thead>
          <tr>
            <th>Persona</th>
            <th>Desde</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {activos.map((o) => (
            <tr key={o.usuarioId}>
              <td>{o.nombre}</td>
              <td>{fechaHora(o.autorizadoEn)}</td>
              <td className="al-der">
                <button
                  type="button"
                  disabled={sacar.isPending}
                  onClick={() => sacar.mutate(o.usuarioId)}
                >
                  Sacar de la caja
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sacar.isError && <PanelError error={sacar.error} />}
    </section>
  );
}

function MovimientoTurno({
  turno,
  tipo,
  alCerrar,
}: {
  turno: TurnoCaja;
  tipo: 'RETIRO' | 'INGRESO';
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [monto, setMonto] = useState('');
  const m = enteroCaja(monto);
  // Sin comentario (Rodrigo, 2026-10-25): el motivo es fijo; queda quién y cuándo.
  const motivo = tipo === 'RETIRO' ? 'Entrega a tesorería' : 'Ingreso de sencillo';
  const registrar = useMutation({
    mutationFn: () => {
      const cuerpo = { tipo, monto: m!, motivo };
      return api.post(`/caja/turnos/${turno.id}/movimientos`, cuerpo, {
        idempotencia: intentos.clave('movimiento-turno', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
      alCerrar();
    },
    onError: (e) => intentos.cerrar(e),
  });
  const valido = !!m && Number(m) > 0 && !registrar.isPending;
  const titulo = tipo === 'RETIRO' ? 'Retiro de efectivo' : 'Ingreso de sencillo';
  return (
    <Modal
      titulo={`${titulo} · ${turno.numeroVisible}`}
      ancho="angosto"
      alCerrar={() => !registrar.isPending && alCerrar()}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={registrar.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={!valido}
            onClick={() => registrar.mutate()}
          >
            {registrar.isPending ? 'Registrando…' : `Registrar ${titulo.toLowerCase()}`}
          </button>
        </>
      }
    >
      <p className="apagado">
        {tipo === 'RETIRO'
          ? 'El efectivo que sale de la gaveta (a tesorería o caja fuerte) baja lo esperado al cierre.'
          : 'El sencillo que entra a la gaveta durante el turno sube lo esperado al cierre.'}
      </p>
      <label className="campo">
        <span className="etiqueta">Monto</span>
        <input
          aria-label="Monto del movimiento"
          data-autofoco
          inputMode="numeric"
          className="cobro-monto"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && valido && registrar.mutate()}
        />
      </label>
      {registrar.isError && <PanelError error={registrar.error} />}
    </Modal>
  );
}

const CONTEOS = [
  { clave: 'efectivo', etiqueta: 'Efectivo en la gaveta (todo, con el fondo)' },
  { clave: 'debito', etiqueta: 'Débito · total del cierre de lote Getnet' },
  { clave: 'credito', etiqueta: 'Crédito · total del cierre de lote Getnet' },
  { clave: 'cheque', etiqueta: 'Cheques · suma de los montos' },
] as const;
type ClaveConteo = (typeof CONTEOS)[number]['clave'];

function CerrarTurno({
  turno,
  alCancelar,
  alCerrar,
}: {
  turno: TurnoCaja;
  alCancelar: () => void;
  alCerrar: (c: CierreTurno) => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [conteo, setConteo] = useState<Record<ClaveConteo, string>>({
    efectivo: '',
    debito: '',
    credito: '',
    cheque: '',
  });
  const [cheques, setCheques] = useState('');
  // Vacío cuenta como cero (Rodrigo, 2026-10-25: «no me deja cerrar»): lo que no se anota es $0.
  const valor = (k: ClaveConteo) => (conteo[k].trim() === '' ? '0' : enteroCaja(conteo[k]));
  const cantidad = cheques.trim() === '' ? 0 : Number(cheques);
  const valido =
    CONTEOS.every((c) => valor(c.clave) !== null) &&
    Number.isInteger(cantidad) &&
    cantidad >= 0 &&
    cantidad <= 999;
  const cerrar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        version: turno.version,
        contado: {
          efectivo: valor('efectivo')!,
          debito: valor('debito')!,
          credito: valor('credito')!,
          cheque: valor('cheque')!,
          chequesCantidad: cantidad,
        },
      };
      return api.post<CierreTurno>(`/caja/turnos/${turno.id}/cerrar`, cuerpo, {
        idempotencia: intentos.clave('cerrar-turno', cuerpo),
      });
    },
    onSuccess: async (c) => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
      alCerrar(c);
    },
    onError: (e) => intentos.cerrar(e),
  });
  const enviar = () => valido && !cerrar.isPending && cerrar.mutate();
  return (
    <Modal
      titulo={`Cerrar turno ${turno.numeroVisible} · arqueo ciego`}
      ancho="angosto"
      alCerrar={() => !cerrar.isPending && alCancelar()}
      pie={
        <>
          <button type="button" onClick={alCancelar} disabled={cerrar.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={!valido || cerrar.isPending}
            onClick={enviar}
          >
            {cerrar.isPending ? 'Cerrando…' : 'Cerrar turno'}
          </button>
        </>
      }
    >
      <p className="apagado">
        Cuenta lo que hay y anótalo. No ves lo esperado: el sistema compara al cerrar. Las
        transferencias no se cuentan aquí (las revisa tesorería en el banco).
      </p>
      <div className="caja-conteo">
        {CONTEOS.map((c, i) => (
          <label className="campo" key={c.clave}>
            <span className="etiqueta">{c.etiqueta}</span>
            <input
              aria-label={c.etiqueta}
              {...(i === 0 ? { 'data-autofoco': true } : {})}
              inputMode="numeric"
              className="cobro-monto"
              placeholder={i === 0 ? '' : '0'}
              value={conteo[c.clave]}
              onChange={(e) => setConteo((x) => ({ ...x, [c.clave]: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && enviar()}
            />
          </label>
        ))}
        <label className="campo">
          <span className="etiqueta">Cantidad de cheques</span>
          <input
            aria-label="Cantidad de cheques"
            inputMode="numeric"
            placeholder="0"
            value={cheques}
            onChange={(e) => setCheques(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && enviar()}
          />
        </label>
      </div>
      {!valido && (
        <p className="aviso-linea tono-aviso" role="alert">
          Revisa los montos: solo números (por ejemplo 125000 o 125.000).
        </p>
      )}
      {cerrar.isError && <PanelError error={cerrar.error} />}
    </Modal>
  );
}

/** Tabla del arqueo (la usan el cajero tras cerrar y tesorería al revisar). */
export function TablaArqueo({
  arqueo,
}: {
  arqueo: {
    medio: MedioCaja;
    esperado: string;
    contado: string | null;
    diferencia: string | null;
    cantidad: number | null;
  }[];
}) {
  return (
    <table className="grilla compacta arqueo" aria-label="Arqueo por medio">
      <thead>
        <tr>
          <th>Medio</th>
          <th className="al-der">Esperado</th>
          <th className="al-der">Contado</th>
          <th className="al-der">Diferencia</th>
        </tr>
      </thead>
      <tbody>
        {arqueo.map((f) => {
          const d = f.diferencia === null ? null : Number(f.diferencia);
          return (
            <tr key={f.medio} data-medio={f.medio}>
              <td>
                {NOMBRE_MEDIO_CAJA[f.medio]}
                {f.medio === 'CHEQUE' && f.cantidad !== null ? ` (${f.cantidad})` : ''}
              </td>
              <td className="al-der">{pesos(f.esperado)}</td>
              <td className="al-der">{f.contado === null ? 'en el banco' : pesos(f.contado)}</td>
              <td
                className="al-der"
                data-diferencia={
                  d === null ? 'no-aplica' : d === 0 ? 'cuadra' : d < 0 ? 'falta' : 'sobra'
                }
              >
                {d === null
                  ? '—'
                  : d === 0
                    ? 'cuadra'
                    : `${d < 0 ? 'falta' : 'sobra'} ${pesos(String(Math.abs(d)))}`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ResultadoCierre({ cierre, alSeguir }: { cierre: CierreTurno; alSeguir: () => void }) {
  const rendir = Number(cierre.aRendirEfectivo);
  return (
    <section className="caja-resultado" aria-label="Turno cerrado" data-cuadra={cierre.cuadra}>
      <h2>
        Turno {cierre.numero} cerrado · {cierre.cuadra ? 'cuadra' : 'con diferencias'}
      </h2>
      <TablaArqueo arqueo={cierre.arqueo} />
      <p className="caja-rendir">
        {rendir >= 0 ? (
          <>
            Efectivo a rendir (lo contado menos el fondo, que queda en la gaveta):{' '}
            <b>{pesos(cierre.aRendirEfectivo)}</b>
          </>
        ) : (
          <>
            La gaveta quedó <b>{pesos(String(-rendir))}</b> bajo el fondo inicial: no hay efectivo
            que rendir al cierre (lo retirado durante el turno ya se entregó).
          </>
        )}
      </p>
      {cierre.entrega && (
        <p className="caja-rendir">
          Entrega a tesorería: <b>{pesos(cierre.entrega.efectivo)}</b> en efectivo
          {cierre.entrega.chequesCantidad > 0 &&
            ` y ${cierre.entrega.chequesCantidad} cheque(s) por ${pesos(cierre.entrega.cheques)}`}
          . Tesorería la cuenta al recibirla.
        </p>
      )}
      {cierre.cierre ? (
        <p data-clase-cierre={cierre.cierre.clase}>
          {cierre.cierre.clase === 'NORMAL'
            ? 'Cierre normal: cuadró sin incidentes y no queda pendiente de revisión.'
            : `Quedó como excepción de nivel ${cierre.cierre.excepcion?.nivel ?? '—'} (${motivosEnTexto(cierre.cierre.excepcion?.motivos)}): la revisa ${nombreCola(cierre.cierre.excepcion?.nivel ?? 1, cierre.cierre.excepcion?.cola ?? 'PRINCIPAL')}.`}
          {cierre.cierre.senales?.length
            ? ` Señales (no son fraude): ${senalesEnTexto(cierre.cierre.senales)}.`
            : ''}
        </p>
      ) : (
        <p className="apagado">
          Tesorería revisa el cierre: si el dinero rendido no calza, queda con reparo.
        </p>
      )}
      <footer className="acciones">
        <button type="button" onClick={() => window.print()}>
          Imprimir comprobante
        </button>
        <button type="button" className="primario" autoFocus onClick={alSeguir}>
          Listo
        </button>
      </footer>
    </section>
  );
}
